import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import classes from './PostEditorBulkImport.module.scss';
import {
  PostEditorContextEnum,
  PostEditorSubmitData,
  PostEditorSubmitErrorPayload,
  PostEditorSubmitPayload
} from '../../../models/post-editor';
import { SubmitErrorHandler, useForm, UseFormReturn } from 'react-hook-form';
import { FormOptions } from '../../../constants/form';
import { usePostEditorNew } from '../../../hooks/usePostEditorNew';
import { useSelectedOrganization } from '../../../hooks/useSelectedOrganization';
import { AppContext } from '../../../providers/AppProvider';
import {
  OnSubmitErrorPostEditorFunction,
  OnSubmitPostEditorFunction
} from '../PostEditorModal/PostEditorModal.controller';
import { SubmitHandler } from 'react-hook-form/dist/types/form';
import { showToast } from '../../../helpers/showToast';
import {
  GetLinkMetadataQuery,
  PostQueuePosition,
  useBulkImportMutation,
  useGetLinkMetadataLazyQuery,
  UtmInput
} from '../../../models/graphql';
import { PostEditorModal } from '../PostEditorModal';
import { PostEditorProvider } from '../states/PostEditorProvider';
import { PostEditorBulkImportGroups } from '../PostEditorBulkImportGroups';
import { BulkImportModal } from '../BulkImportModal';
import { Button } from '../../../components/actions/Button';
import { ButtonColorsEnum } from '../../../constants/button-colors';
import { BulkActionsProvider } from '../states/BulkActionsProvider';
import { useSettings } from '../../../hooks/useSettings';
import { GENERIC_ERROR_MESSAGE } from '../../../constants/apollo-errors';
import { triggerErrorToast } from '../../../helpers/triggerErrorToast';
import { setProfileSelectorFormValues } from '../../../components/socialProfiles/SocialProfileSelector/SocialProfileSelector.view';
import { BiggerScreenPlaceholder } from '../../../components/containers/BiggerScreenPlaceHolder';
import { useBreakpoints } from '../../../hooks/useBreakpoints';
import { useClosePostEditor } from '../../../hooks/useClosePostEditor';

export const PostEditorBulkImportView = (): JSX.Element => {
  const smallScreen = useBreakpoints() === 'small';
  const { postEditorContext, setPollUsage } = useContext(AppContext);
  const { closePostEditor } = useClosePostEditor();
  const ref = useRef<HTMLDivElement | null>(null);
  if (postEditorContext.type !== PostEditorContextEnum.import)
    throw new Error(
      'Post Editor Bulk Import has been called with wrong context value: ' + JSON.stringify(postEditorContext)
    );
  const formInstance: UseFormReturn = useForm({
    ...FormOptions
  });
  const overrideWorkspaceSettings = !!postEditorContext.overrideWorkspaceSettings;
  const { loading, initialState, profiles, categories, error } = usePostEditorNew(
    postEditorContext.initialState,
    overrideWorkspaceSettings
  );
  const submitted = useRef<boolean>(false);

  // Set form state for the profiles after loading.
  useEffect(() => {
    if (!loading && profiles && initialState) {
      for (const [groupIndex, postGroup] of Object.entries(initialState.postGroups)) {
        const activeProfiles = postGroup.profiles;
        setProfileSelectorFormValues(profiles, activeProfiles, formInstance, `postGroup_${groupIndex}_profiles`);
      }
    }
  }, [formInstance, initialState, loading, profiles]);

  const bulkImportHookResult = useBulkImportMutation();
  const { selectedWorkspace } = useSelectedOrganization();
  const { selectedWorkspaceSettings } = useSettings();
  const [getLinksMetadata, getLinksMetadataHookResult] = useGetLinkMetadataLazyQuery();
  const { data: getLinksMetadataResult, error: getLinksMetadataError } = getLinksMetadataHookResult;

  const utm: UtmInput = useMemo(() => {
    const userSettings = selectedWorkspace?.settings;
    return {
      campaign: userSettings?.utm?.campaign || '',
      content: userSettings?.utm?.content || '',
      medium: userSettings?.utm?.medium || '',
      source: userSettings?.utm?.source || '',
      term: userSettings?.utm?.term || ''
    };
  }, [selectedWorkspace]);

  const evergreenSettings = selectedWorkspaceSettings?.evergreenRecycling || false;
  const positionInQueueSettings = selectedWorkspaceSettings?.defaultQueuePosition || PostQueuePosition.TOP;

  const onSubmitHandler: OnSubmitPostEditorFunction<SubmitHandler<PostEditorSubmitData>> =
    (dispatch, profiles) => async data => {
      try {
        if (!selectedWorkspace || profiles === undefined) {
          throw new Error('No Selected Workspace or selected profiles are undefined.');
        }
        const payload: PostEditorSubmitPayload = {
          ref,
          submitted,
          profiles,
          workspaceId: selectedWorkspace.id,
          data,
          type: 'bulk',
          context: 'create',
          callback: () => {
            closePostEditor();
            setPollUsage({ amountPoll: 5, interval: 60000, shouldPoll: true });
            // to update the post count usage after the bulk import is done
          },
          defaultQueuePosition: positionInQueueSettings,
          bulkImportHookResult
        };
        dispatch({ type: 'submitEditor', payload });
      } catch (e) {
        showToast({
          summary: 'Submit Error',
          detail: (e as Error).message,
          severity: 'error'
        });
      }
    };

  const onErrorHandler: OnSubmitErrorPostEditorFunction<SubmitErrorHandler<PostEditorSubmitData>> = dispatch => () => {
    const payload: PostEditorSubmitErrorPayload = {
      type: 'single'
    };
    submitted.current = false;
    dispatch({ type: 'submitErrorEditor', payload });
  };

  const [initialised, setInitialised] = useState<boolean>(false);
  const [bulkImportModal, setBulkImportModal] = useState<boolean>(false);
  const [linksMetadata, setLinksMetadata] = useState<undefined | GetLinkMetadataQuery['linkMetadata']>(undefined);

  useEffect(() => {
    if (error) {
      closePostEditor();
    }
  }, [error, closePostEditor]);

  useEffect(() => {
    if (!initialised || !initialState) return;
    const links = initialState.postGroups.reduce((prevLinks: string[], current) => {
      const { posts, groupPostAttributes, customizeContentPerProfile } = current;
      if (customizeContentPerProfile) {
        const postsLinks = posts.reduce((prevPostLinks: string[], currPost) => {
          const { link } = currPost.content;
          if (!link) return prevLinks;
          return prevPostLinks.includes(link.initialUrl) || prevLinks.includes(link.initialUrl)
            ? prevPostLinks
            : [...prevPostLinks, link.initialUrl];
        }, []);
        return [...prevLinks, ...postsLinks];
      } else {
        const { link } = groupPostAttributes.content;
        if (!link) return prevLinks;
        return prevLinks.includes(link.initialUrl) ? prevLinks : [...prevLinks, link.initialUrl];
      }
    }, []);
    if (links.length) {
      getLinksMetadata({
        variables: {
          urls: links
        }
      }).catch(() => {
        // this will never be triggered, but since it's a promise, we should have catch
        showToast({
          summary: 'Error fetching link metadata',
          severity: 'error',
          detail: GENERIC_ERROR_MESSAGE
        });
      });
    } else {
      setLinksMetadata([]);
    }
  }, [getLinksMetadata, initialised, initialState]);

  useEffect(() => {
    if (getLinksMetadataError) {
      setLinksMetadata([]);
      triggerErrorToast('Error Fetching Links Metadata')(getLinksMetadataError);
    }
  }, [getLinksMetadataError]);

  useEffect(() => {
    if (getLinksMetadataResult?.linkMetadata) {
      setLinksMetadata(getLinksMetadataResult.linkMetadata);
    }
  }, [getLinksMetadataResult]);

  const title = (
    <div className={'grid-x align-middle'}>
      <div className={'cell shrink'}>
        <h3 className={classes.Title}>Bulk Import</h3>
      </div>
      <div className={'cell shrink'}>
        <div className={classes.BulkActionsButtonWrapper}>
          <Button
            loading={linksMetadata === undefined}
            type={'a'}
            variant={'solid'}
            size={'medium'}
            color={ButtonColorsEnum.primary}
            action={() => setBulkImportModal(true)}>
            Bulk Actions
          </Button>
        </div>
      </div>
    </div>
  );

  if (smallScreen) return <BiggerScreenPlaceholder />;

  return (
    <>
      {!initialised ? (
        <PostEditorModal
          title={title}
          formInstance={formInstance}
          loading={true}
          onErrorHandler={onErrorHandler}
          onSubmitHandler={onSubmitHandler}>
          <></>
        </PostEditorModal>
      ) : null}
      {initialState && !loading ? (
        <PostEditorProvider
          initialState={initialState}
          loading={loading}
          categories={categories}
          profiles={profiles}
          {...(postEditorContext.payload ? { payload: postEditorContext.payload } : {})}>
          <PostEditorModal
            title={title}
            formInstance={formInstance}
            loading={loading}
            setInitialised={setInitialised}
            onErrorHandler={onErrorHandler}
            onSubmitHandler={onSubmitHandler}>
            <PostEditorBulkImportGroups ref={ref} formInstance={formInstance} linksMetadata={linksMetadata} />
          </PostEditorModal>
          {bulkImportModal && linksMetadata !== undefined ? (
            <BulkActionsProvider
              profiles={profiles}
              categories={categories}
              utm={utm}
              evergreenSettings={evergreenSettings}
              linksMetadata={linksMetadata}
              positionInQueueSettings={positionInQueueSettings}>
              <BulkImportModal setBulkImportModal={setBulkImportModal} />
            </BulkActionsProvider>
          ) : null}
        </PostEditorProvider>
      ) : null}
    </>
  );
};
